import { UserProfile } from '../modules/userProfile/userProfileTypes'

type UserProfileNameFields = Pick<
  UserProfile,
  'firstName' | 'lastName' | 'givenName' | 'familyName'
>

type UserProfileCreatedByFields = UserProfileNameFields &
  Pick<UserProfile, 'email'>

type UserListEntry = { id: string; name?: string }

export const getUserProfileFullName = (
  user?: UserProfileNameFields | null
): string =>
  `${user?.firstName || user?.givenName || ''} ${
    user?.lastName || user?.familyName || ''
  }`.trim()

/** Formats creator as `Full Name <email>`, falling back to name or email alone. */
export const formatCreatedByDisplay = (
  user?: UserProfileCreatedByFields | null
): string => {
  const username = getUserProfileFullName(user)
  return username && user?.email
    ? `${username} <${user.email}>`
    : username || user?.email || ''
}

/**
 * CRM service: `created_by` stays a UUID and `created_by_display` holds
 * `Name <email>`. Mapping service: `created_by` is the display string.
 */
export const buildCreatedByPayload = ({
  userId,
  createdByDisplay,
  forMappingService,
}: {
  userId: string
  createdByDisplay: string
  forMappingService: boolean
}): { createdBy: string; createdByDisplay?: string } =>
  forMappingService
    ? { createdBy: createdByDisplay }
    : { createdBy: userId, createdByDisplay }

/**
 * Same split as create: CRM keeps UUID on `updated_by` and stores the
 * display string on `updated_by_display`; mapping service uses the display
 * string on `updated_by`.
 */
export const buildUpdatedByPayload = ({
  userId,
  updatedByDisplay,
  forMappingService,
}: {
  userId: string
  updatedByDisplay: string
  forMappingService: boolean
}): { updatedBy: string; updatedByDisplay?: string } =>
  forMappingService
    ? { updatedBy: updatedByDisplay }
    : { updatedBy: userId, updatedByDisplay }

/**
 * Prefer CRM `created_by_display`, then partner user-list name, then the raw
 * `created_by` value (already `Name <email>` from mapping service).
 */
export const resolveTemplateCreatedByDisplay = (
  template?: {
    createdBy?: string
    createdByDisplay?: string
  } | null,
  users?: UserListEntry[] | null
): string => {
  const display = template?.createdByDisplay?.trim()
  if (display) {
    return display
  }

  const createdById = template?.createdBy
  if (!createdById) {
    return '-'
  }

  const matchedName = users?.find(user => user.id === createdById)?.name?.trim()
  if (matchedName) {
    return matchedName
  }

  return createdById
}
